import React, { useState } from "react";
import OTPInput from "react-otp-input";
import ResendOTP from "../../../helpers/Common/ResendOtp";
import { Spinner } from "reactstrap";
import { CustomSnackbar } from "../../../helpers/CustomSnackbar";
import { Link, useNavigate } from "react-router-dom";
import { apiErrorHandler } from "../../../helpers/common";
import {
  verifyEmailForgotPasswordOtp,
  verifyEmailOtp,
} from "../../../api/customer";
import { useDispatch } from "react-redux";
import PropTypes from "prop-types";

const VerifyEmail = ({
  formData,
  showLoginBtn = true,
  source = "signup",
  showFullDescription = false,
  onVerifyEmailSuccess,
  isMultiStep = false,
}) => {
  const [otp, setOtp] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isSourceResetPassword = source === "resetPassword";

  const handleVerifyEmail = async () => {
    if (!otp) {
      CustomSnackbar.error("Please enter OTP!");
      return;
    }

    if (otp?.length < 6) {
      CustomSnackbar.error("Invalid OTP!");
      return;
    }

    setIsLoading(true);
    try {
      const payload = {
        email: formData?.email,
        email_verification_otp: otp,
      };

      let response = null;

      if (isSourceResetPassword) {
        response = await verifyEmailForgotPasswordOtp(payload);
      } else {
        response = await verifyEmailOtp(payload);
      }

      if (isSourceResetPassword && response?.data?.message) {
        CustomSnackbar.success(response?.data?.message);
        navigate("/signin");
        return;
      }

      if (response?.data?.token) {
        CustomSnackbar.success(
          source === "login" ? "Login Successfully" : "Email verified successfully."
        );
        
        if (isMultiStep) {
          // For multi-step verification, proceed with login and navigation
          dispatch({ type: "LOGIN_SUCCESS", payload: response?.data });

          if (onVerifyEmailSuccess) {
            onVerifyEmailSuccess();
          }

          // Navigate based on customer type
          if (response?.data?.customer?.customer_type === 1) {
            navigate("/employer/company/manage");
          } else {
            navigate("/candidate/profile/update");
          }
        } else {
          // For single step verification, proceed with login
          dispatch({ type: "LOGIN_SUCCESS", payload: response?.data });

          if (onVerifyEmailSuccess) {
            onVerifyEmailSuccess();
          }

          if (response?.data?.customer?.customer_type === 1) {
            navigate("/employer/company/manage");
          } else {
            navigate("/candidate/profile/update");
          }
        }
      }
    } catch (error) {
      apiErrorHandler(error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className='w-100 verification-container'>
      <div className='verification-card'>
        <div className='text-center mb-4'>
          <h4 className='verification-title'>
            {isSourceResetPassword ? "Verify Email OTP" : "Verify Email Address"}
          </h4>
          <p className='verification-subtitle'>
            An OTP has been sent to your email address <b>{formData?.email}</b>.
            {showFullDescription &&
              "Please verify your email to continue using your account."}
          </p>
        </div>

        <div className="otp-container">
          <OTPInput
            value={otp}
            onChange={setOtp}
            numInputs={6}
            renderSeparator={<span style={{ margin: 4 }}>-</span>}
            containerStyle={{ 
              display: "flex", 
              justifyContent: "center", 
              alignItems: "center", 
              flexWrap: "nowrap",
              gap: "3px",
              marginBottom: "15px",
              padding: "0 5px",
              maxWidth: "100%"
            }}
            inputType='number'
            renderInput={(props) => (
              <input
                {...props}
                className={`otp-input`}
                style={{ flexShrink: 0 }}
              />
            )}
          />
        </div>

        <div className='resend-otp-container'>
          <ResendOTP email={formData?.email} maxTime={60} />
        </div>

        <div className='text-center mt-4'>
          {isLoading ? (
            <button
              className='verify-btn w-50 disabled'
              type='button'
              disabled
            >
              <Spinner size="sm" className="me-2" />
              Verify Email
            </button>
          ) : (
            <button
              className='verify-btn w-50'
              type='button'
              onClick={handleVerifyEmail}
            >
              Verify Email
            </button>
          )}
        </div>

        {showLoginBtn && (
          <div className='text-center mt-3'>
            <Link to='/signin' className='resend-otp-button'>
              Go to login
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

VerifyEmail.propTypes = {
  formData: PropTypes.object,
  showLoginBtn: PropTypes.bool,
  source: PropTypes.oneOf(["signup", "login", "resetPassword"]),
  isMultiStep: PropTypes.bool,
};

export default VerifyEmail;
